/* HDFC Impact Dashboard - pure calculation module (no DOM).
   mode 'pbi'       : reproduces the Power BI DAX model, known defects included.
   mode 'corrected' : sources averaged by responses (D1, D2), zero scores dropped (D8),
                      missing indicators rescaled out (D4), gapless bands (D7). */
const HC = (() => {
  const TOOL_ORDER = ["Health", "NRM", "POE", "SDLE"];   // Power BI column order
  const SRC_LABEL = { thematic: "SurveyCTO thematic form", short_long: "SurveyCTO short & long-term form", poe: "SurveyCTO POE scholarship form", study_closure: "SurveyCTO study closure form" };
  let M = null; // model

  function decode(o, strKeys, S) {
    const keys = Object.keys(o), n = o[keys[0]].length, out = new Array(n);
    for (let i = 0; i < n; i++) {
      const r = {};
      for (const k of keys) { const v = o[k][i]; r[k] = strKeys.includes(k) ? (v < 0 || v === null ? "" : S[v]) : v; }
      out[i] = r;
    }
    return out;
  }

  function init(data) {
    const S = data.strs;
    const comb = decode(data.comb, ["p", "tool", "ind", "par", "intv", "src"], S);
    const ind = decode(data.ind, ["p", "tool", "ind"], S);
    const act = decode(data.act, ["p", "tool", "ind", "intv", "cat", "act", "par"], S);
    const parInd = {}, parType = {};
    for (const r of comb) {
      if (r.par && r.ind && !parInd[r.par]) parInd[r.par] = r.ind;
      if (r.par) parType[r.par] = r.src === "study_closure" ? "Qualitative" : (parType[r.par] || "Quantitative");
    }
    const ipBy = {}; for (const r of data.ip) ipBy[r.p] = r;
    const hasData = new Set(comb.map(r => r.p));
    M = { data, comb, ind, act, ip: data.ip, ipBy, pw: data.pw, seq: data.seq, parInd, parType, hasData, links: data.links || [], meta: data.meta };
    return M;
  }

  const seqOf = i => M.seq[i] || 99;
  const toolSort = (a, b) => (TOOL_ORDER.indexOf(a) + 1 || 9) - (TOOL_ORDER.indexOf(b) + 1 || 9) || a.localeCompare(b);
  const uniq = a => [...new Set(a)];
  const mean = a => a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;

  // corrected: round to 1 dp, then gapless bands. pbi: the rule in HDFC's scoring guide / Power BI
  // formatting (2 dp, strictly greater than 4.6 / 3.5 / 2.5), so 4.60 shows as Good.
  function band(x, mode) {
    if (x === null || x === undefined || isNaN(x)) return null;
    if (mode === "pbi") {
      const r = Math.round((x + 1e-9) * 100) / 100;
      return r > 4.6 ? "Excellent" : r > 3.5 ? "Good" : r > 2.5 ? "Moderate" : "Poor";
    }
    const r = Math.round(x * 10) / 10;
    return r >= 4.6 ? "Excellent" : r >= 3.6 ? "Good" : r >= 2.6 ? "Moderate" : "Poor";
  }

  /* ---------- Responses submission status (SurveyCTO review workflow) ---------- */
  const NONE = "__none__";
  function submissions(f) {
    const closed = new Set(M.data.closed || []);
    const g = {};
    for (const s of (M.data.status || [])) {
      const ip = M.ipBy[s.p] || {};
      const rid = ip.rid || "";
      if (f.rid && rid !== (f.rid === NONE ? "" : f.rid)) continue;
      if (f.p && s.p !== f.p) continue;
      const k = rid + "|" + s.p + "|" + s.tool;
      const o = g[k] || (g[k] = { rid, p: s.p, tool: s.tool, pending: 0, approved: 0, forms: [], closure: closed.has(s.p) ? "Yes" : "No" });
      o.pending += s.pending; o.approved += s.approved; if (!o.forms.includes(s.form)) o.forms.push(s.form);
    }
    const rows = Object.values(g).sort((a, b) => (b.rid ? 1 : 0) - (a.rid ? 1 : 0) || b.rid.localeCompare(a.rid) || a.p.localeCompare(b.p) || toolSort(a.tool, b.tool));
    const tot = rows.reduce((a, r) => ({ pending: a.pending + r.pending, approved: a.approved + r.approved }), { pending: 0, approved: 0 });
    const all = M.data.status || [];
    const ridOf = s => (M.ipBy[s.p] || {}).rid || "";
    const rids = uniq(all.map(ridOf).filter(Boolean)).sort();
    // projects with submissions but no row in Implementing_Partners get their own option
    if (all.some(s => !ridOf(s))) rids.push([NONE, "Not mapped to an agency"]);
    const projects = uniq(all.filter(s => !f.rid || ridOf(s) === (f.rid === NONE ? "" : f.rid)).map(s => s.p)).sort();
    return { rows, tot, rids, projects };
  }

  /* ---------- selections ---------- */
  function fyOptions() { return uniq(M.ip.map(r => r.fy)).sort().reverse(); }
  function ridOptions(fy) { return uniq(M.ip.filter(r => !fy || r.fy === fy).map(r => r.rid)).sort(); }
  function projectsFor(fy, rid) {
    let ps = (!fy && !rid) ? [...M.hasData] : M.ip.filter(r => (!fy || r.fy === fy) && (!rid || r.rid === rid)).map(r => r.p);
    return uniq(ps).filter(p => M.hasData.has(p)).sort();
  }
  function projSet(f) { return new Set(f.p ? [f.p] : projectsFor(f.fy, f.rid)); }
  function filteredComb(f) {
    const P = projSet(f);
    return M.comb.filter(r => P.has(r.p) && (!f.tool || r.tool === f.tool) && (!f.ind || r.ind === f.ind) && (!f.intv || r.intv === f.intv));
  }

  /* ---------- parameter level ---------- */
  // corrected: response-weighted mean of every valid row, all sources pooled
  // pbi: per source plain mean of row averages, then SUM across sources (D1)
  function paramScores(rows, mode) {
    const g = {};
    for (const r of rows) {
      if (!r.par) continue;
      const k = r.p + "|" + r.tool + "|" + r.ind + "|" + r.par;
      const o = g[k] || (g[k] = { p: r.p, tool: r.tool, ind: r.ind, par: r.par, wx: 0, wr: 0, r: 0, src: {} });
      o.wx += r.wx || 0; o.wr += r.wr || 0; o.r += r.r || 0;
      const s = o.src[r.src] || (o.src[r.src] = { n: 0, s: 0, r: 0 });
      s.n += r.n; s.s += r.s || 0; s.r += r.r || 0;
    }
    for (const k in g) {
      const o = g[k];
      if (mode === "corrected") o.score = o.wr > 0 ? o.wx / o.wr : null;
      else {
        let tot = null;
        for (const sk in o.src) {
          const s = o.src[sk];
          const counts = sk === "study_closure" ? s.n > 0 : (s.n > 0 && s.r > 0);
          if (counts) tot = (tot || 0) + s.s / s.n;
        }
        o.score = tot;
      }
      o.resp = mode === "corrected" ? o.wr : o.r;
    }
    return g;
  }

  // indicator score per project x tool x indicator = sum(ps*pw)/sum(pw) over parameters with a score
  function indicatorScores(rows, mode) {
    const ps = paramScores(rows, mode), g = {};
    for (const k in ps) {
      const o = ps[k]; if (o.score === null) continue;
      const w = M.pw[o.par]; if (w === undefined) continue;
      const kk = o.p + "|" + o.tool + "|" + o.ind;
      const t = g[kk] || (g[kk] = { p: o.p, tool: o.tool, ind: o.ind, num: 0, den: 0, resp: 0, params: [] });
      t.num += o.score * w; t.den += w; t.resp += o.resp; t.params.push(o);
    }
    for (const k in g) g[k].score = g[k].num / g[k].den;
    return { ind: g, par: ps };
  }

  /* ---------- project score ---------- */
  function projectScore(f, mode) {
    const P = projSet(f);
    const wrows = M.ind.filter(r => P.has(r.p) && (!f.tool || r.tool === f.tool) && (!f.ind || r.ind === f.ind));
    if (mode === "pbi") {
      // SUM(project_score_oecd[Weighted_Score]) / DISTINCTCOUNT(project_score_oecd[tool])
      const has = wrows.filter(r => r.sp !== null);
      if (!has.length) return { score: null, coverage: null, perProject: {} };
      const num = has.reduce((a, r) => a + r.sp * r.w, 0);
      const den = uniq(wrows.map(r => r.tool)).length;
      const perProject = {};
      for (const p of P) {
        const pr = wrows.filter(r => r.p === p), h = pr.filter(r => r.sp !== null);
        perProject[p] = h.length ? h.reduce((a, r) => a + r.sp * r.w, 0) / uniq(pr.map(r => r.tool)).length : null;
      }
      const covW = wrows.reduce((a, r) => a + (r.sp !== null ? r.w : 0), 0), allW = wrows.reduce((a, r) => a + r.w, 0);
      return { score: num / den, coverage: allW ? covW / allW : null, perProject, sumsAcross: P.size > 1 };
    }
    const rows = filteredComb(f);
    const { ind } = indicatorScores(rows, "corrected");
    const perProject = {}, perCov = {};
    let covW = 0, allW = 0;
    for (const p of P) {
      const pr = wrows.filter(r => r.p === p), tools = uniq(pr.map(r => r.tool)), tScores = [];
      let pc = 0, pa = 0;
      for (const t of tools) {
        let num = 0, den = 0;
        for (const r of pr.filter(x => x.tool === t)) {
          const is = ind[p + "|" + t + "|" + r.ind];
          pa += r.w;
          if (is) { num += is.score * r.w; den += r.w; pc += r.w; }
        }
        if (den > 0) tScores.push(num / den);
      }
      perProject[p] = mean(tScores); perCov[p] = pa ? pc / pa : null; covW += pc; allW += pa;
    }
    const vals = Object.values(perProject).filter(v => v !== null);
    return { score: mean(vals), coverage: allW ? covW / allW : null, perProject, perCov };
  }

  /* ---------- OECD page ---------- */
  function oecd(f, mode) {
    const rows = filteredComb(f);
    const totalResp = rows.reduce((a, r) => a + (r.r || 0), 0);
    const tools = uniq(rows.map(r => r.tool)).filter(Boolean).sort(toolSort);
    const proj = projectScore(f, mode);

    // parameter matrix (Indicator > Parameter x Tool)
    const cell = {};
    for (const r of rows) {
      if (!r.par) continue;
      for (const t of [r.tool, "__all"]) {
        const k = r.ind + "|" + r.par + "|" + t;
        const c = cell[k] || (cell[k] = { n: 0, s: 0, r: 0, wx: 0, wr: 0, srcs: new Set() });
        c.n += r.n; c.s += r.s || 0; c.r += r.r || 0; c.wx += r.wx || 0; c.wr += r.wr || 0; if (r.n > 0) c.srcs.add(r.src);
      }
    }
    // pbi parameter cell = ROUND(AVERAGE(combine_table[Score]),2) (Response) ; corrected = response-weighted mean
    const pcell = (ind, par, t) => {
      const c = cell[ind + "|" + par + "|" + t]; if (!c) return null;
      const v = mode === "pbi" ? (c.n ? c.s / c.n : null) : (c.wr ? c.wx / c.wr : null);
      return { v, resp: mode === "pbi" ? c.r : c.wr, srcs: [...c.srcs] };
    };
    let pars;
    if (mode === "pbi") pars = uniq(rows.filter(r => r.par).map(r => r.ind + "|" + r.par));
    else pars = Object.keys(M.parInd).filter(p => !f.ind || M.parInd[p] === f.ind).map(p => M.parInd[p] + "|" + p);
    pars.sort((a, b) => { const [ia, pa] = a.split("|"), [ib, pb] = b.split("|"); return seqOf(ia) - seqOf(ib) || (M.pw[pb] || 0) - (M.pw[pa] || 0) || pa.localeCompare(pb); });
    const paramRows = pars.map(k => { const [ind, par] = k.split("|"); return { ind, par, w: M.pw[par], type: M.parType[par], cells: Object.fromEntries(tools.map(t => [t, pcell(ind, par, t)])), all: pcell(ind, par, "__all") }; });

    // indicator matrix
    const indsAll = uniq([...rows.map(r => r.ind).filter(Boolean), ...(mode === "corrected" ? Object.values(M.parInd) : [])]).filter(i => !f.ind || i === f.ind).sort((a, b) => seqOf(a) - seqOf(b));
    let indRows;
    if (mode === "pbi") {
      // AVERAGE(combine_table[parameter_weightage_score]) where pws = Score * LOOKUPVALUE(weight by parameter)
      const g = {};
      for (const r of rows) {
        const w = M.pw[r.par]; if (w === undefined || !r.n) continue;
        for (const t of [r.tool, "__all"]) { const k = r.ind + "|" + t; const o = g[k] || (g[k] = { s: 0, n: 0 }); o.s += (r.s || 0) * w; o.n += r.n; }
      }
      indRows = indsAll.map(i => ({ ind: i, cells: Object.fromEntries(tools.map(t => [t, g[i + "|" + t] ? g[i + "|" + t].s / g[i + "|" + t].n : null])), all: g[i + "|__all"] ? g[i + "|__all"].s / g[i + "|__all"].n : null }));
    } else {
      const { ind } = indicatorScores(rows, "corrected");
      const vals = Object.values(ind);
      indRows = indsAll.map(i => {
        const cells = {};
        for (const t of tools) cells[t] = mean(vals.filter(o => o.ind === i && o.tool === t).map(o => o.score));
        return { ind: i, cells, all: mean(vals.filter(o => o.ind === i).map(o => o.score)) };
      });
    }
    // indicator profile (1-5 scale both modes)
    const P = projSet(f);
    const wrows = M.ind.filter(r => P.has(r.p) && (!f.tool || r.tool === f.tool) && (!f.ind || r.ind === f.ind));
    // 1-5 indicator score per tool (same source as the profile chart), used to colour the weightage cells
    {
      const sc = {};
      for (const r of M.ind) {
        if (!P.has(r.p) || (f.ind && r.ind !== f.ind) || r.sp === null) continue;
        for (const t of [r.tool, "__all"]) { if (t !== "__all" && f.tool && t !== f.tool) continue; const k = r.ind + "|" + t; (sc[k] || (sc[k] = [])).push(r.sp); }
      }
      for (const row of indRows) {
        row.scoreCells = Object.fromEntries(tools.map(t => [t, sc[row.ind + "|" + t] ? mean(sc[row.ind + "|" + t]) : null]));
        row.scoreAll = sc[row.ind + "|__all"] ? mean(sc[row.ind + "|__all"]) : null;
      }
    }
    let profile;
    if (mode === "pbi") {
      profile = uniq(wrows.map(r => r.ind)).sort((a, b) => seqOf(a) - seqOf(b)).map(i => {
        const rs = wrows.filter(r => r.ind === i);
        return { ind: i, v: mean(rs.filter(r => r.sp !== null).map(r => r.sp)), w: mean(rs.map(r => r.w)) };
      });
    } else {
      profile = uniq(wrows.map(r => r.ind)).sort((a, b) => seqOf(a) - seqOf(b)).map(i => ({ ind: i, v: (indRows.find(x => x.ind === i) || {}).all ?? null, w: mean(wrows.filter(r => r.ind === i).map(r => r.w)) }));
    }
    // projects table
    const projRows = [...P].map(p => {
      const ip = M.ipBy[p] || {};
      const pp = projectScore({ ...f, p }, "pbi").score, pc = projectScore({ ...f, p }, "corrected");
      const resp = rows.filter(r => r.p === p).reduce((a, r) => a + (r.r || 0), 0);
      return { p, rid: ip.rid || "", partner: ip.partner || "", state: (ip.state || "").trim(), type: ip.type || "", pbi: pp, corrected: pc.score, coverage: pc.coverage, resp };
    });
    return { totalResp, tools, proj, paramRows, indRows, profile, projRows, nProjects: P.size };
  }

  /* ---------- Activity page ---------- */
  function activity(f, mode) {
    const P = new Set(f.p ? [f.p] : (f.rid ? M.ip.filter(r => r.rid === f.rid).map(r => r.p) : M.act.map(r => r.p)));
    const q = (f.q || "").toLowerCase();
    const rows = M.act.filter(r => P.has(r.p) && (!q || (r.p + " " + r.tool + " " + r.ind + " " + r.intv + " " + r.cat + " " + r.act + " " + r.par).toLowerCase().includes(q)));
    rows.sort((a, b) => a.p.localeCompare(b.p) || toolSort(a.tool, b.tool) || seqOf(a.ind) - seqOf(b.ind) || a.intv.localeCompare(b.intv) || a.act.localeCompare(b.act));
    const tot = { resp: 0, v5: 0, v4: 0, v3: 0, v2: 0, v1: 0 };
    let sAvg = 0, nAvg = 0, wx = 0, wr = 0;
    for (const r of rows) {
      tot.resp += r.resp || 0; for (const k of ["v5", "v4", "v3", "v2", "v1"]) tot[k] += r[k] || 0;
      if (r.avg !== null) { sAvg += r.avg; nAvg++; if (r.avg > 0 && r.resp > 0) { wx += r.avg * r.resp; wr += r.resp; } }
    }
    tot.avg = mode === "pbi" ? (nAvg ? sAvg / nAvg : null) : (wr ? wx / wr : null);
    // slicer lists only projects that have at least one activity row to show
    // (20 thematic projects have no scored rows; listing them produced an empty table)
    const pool = uniq(M.act.map(r => r.p));
    const projects = pool.filter(p => !f.rid || (M.ipBy[p] && M.ipBy[p].rid === f.rid)).sort();
    return { rows, tot, projects };
  }

  /* ---------- Score logic: activity -> project flow ---------- */
  function trace(p, mode) {
    const rows = M.comb.filter(r => r.p === p);
    const wr = M.ind.filter(r => r.p === p);
    const tools = uniq(wr.map(r => r.tool)).sort(toolSort);
    const { ind, par } = indicatorScores(rows, mode);
    const toolOut = [];
    for (const t of tools) {
      const inds = wr.filter(r => r.tool === t).sort((a, b) => seqOf(a.ind) - seqOf(b.ind)).map(r => {
        const is = ind[p + "|" + t + "|" + r.ind];
        return { ind: r.ind, w: r.w, score: is ? is.score : null, resp: is ? is.resp : 0, params: is ? is.params : [], pbiStored: r.sp, corrStored: r.sc };
      });
      const avail = inds.filter(i => i.score !== null), wAvail = avail.reduce((a, i) => a + i.w, 0), wAll = inds.reduce((a, i) => a + i.w, 0);
      const num = avail.reduce((a, i) => a + i.score * i.w, 0);
      const score = !avail.length ? null : mode === "pbi" ? num : num / wAvail;
      toolOut.push({ tool: t, inds, wAvail, wAll, score, hasData: avail.length > 0 });
    }
    const withData = toolOut.filter(t => t.hasData);
    const nT = mode === "pbi" ? tools.length : withData.length;
    const project = !withData.length ? null : mode === "pbi" ? withData.reduce((a, t) => a + t.score, 0) / tools.length : mean(withData.map(t => t.score));

    // flows in score points: every column sums to the project score
    const links = [], nodes = {};
    const node = (id, col, label, sub, meta) => nodes[id] || (nodes[id] = { id, col, label, sub, meta: meta || {}, value: 0 });
    const projId = "proj"; node(projId, 4, p, "Project score");
    for (const t of withData) {
      const f = 1 / nT, tId = "tool|" + t.tool;
      node(tId, 3, t.tool, "Tool score " + t.score.toFixed(2));
      for (const i of t.inds.filter(x => x.score !== null)) {
        const iw = mode === "pbi" ? i.w : i.w / t.wAvail;
        const iVal = i.score * iw * f, iId = "ind|" + t.tool + "|" + i.ind;
        node(iId, 2, i.ind, (withData.length > 1 ? t.tool + " · " : "") + "score " + i.score.toFixed(2) + " · weight " + i.w.toFixed(2), { ind: i.ind });
        links.push({ s: iId, t: tId, v: iVal, ind: i.ind });
        const pAvail = i.params.reduce((a, q) => a + M.pw[q.par], 0);
        for (const q of i.params) {
          const pw = M.pw[q.par] / pAvail, pVal = q.score * pw * iw * f, pId = "par|" + t.tool + "|" + q.par;
          node(pId, 1, q.par, "score " + q.score.toFixed(2) + " · weight " + M.pw[q.par].toFixed(2), { ind: i.ind, par: q.par, tool: t.tool });
          links.push({ s: pId, t: iId, v: pVal, ind: i.ind });
          // source x intervention parts
          const parts = rows.filter(r => r.tool === t.tool && r.ind === i.ind && r.par === q.par);
          if (mode === "corrected") {
            for (const r of parts) if (r.wr > 0) {
              const v = (r.wx / q.wr) * pw * iw * f, sId = "src|" + t.tool + "|" + r.src + "|" + r.intv;
              node(sId, 0, r.intv || SRC_LABEL[r.src], SRC_LABEL[r.src], { src: r.src });
              links.push({ s: sId, t: pId, v, ind: i.ind, resp: r.wr });
            }
          } else {
            const bySrc = {};
            for (const r of parts) (bySrc[r.src] = bySrc[r.src] || []).push(r);
            for (const sk in bySrc) {
              const s = q.src[sk]; const counts = sk === "study_closure" ? s.n > 0 : (s.n > 0 && s.r > 0); if (!counts) continue;
              for (const r of bySrc[sk]) if (r.n > 0) {
                const v = (r.s / s.n) * pw * iw * f, sId = "src|" + t.tool + "|" + r.src + "|" + r.intv;
                node(sId, 0, r.intv || SRC_LABEL[r.src], SRC_LABEL[r.src], { src: r.src });
                links.push({ s: sId, t: pId, v, ind: i.ind, resp: r.r });
              }
            }
          }
        }
      }
      links.push({ s: tId, t: projId, v: t.score * f, ind: null });
    }
    for (const l of links) { nodes[l.s].out = (nodes[l.s].out || 0) + l.v; nodes[l.t].in = (nodes[l.t].in || 0) + l.v; }
    for (const id in nodes) nodes[id].value = Math.max(nodes[id].in || 0, nodes[id].out || 0);
    return { p, ip: M.ipBy[p] || {}, mode, tools: toolOut, project, nTools: tools.length, nToolsWithData: withData.length, nodes: Object.values(nodes), links, par };
  }

  function weightsFor(p) {
    const wr = M.ind.filter(r => r.p === p).sort((a, b) => toolSort(a.tool, b.tool) || seqOf(a.ind) - seqOf(b.ind));
    const params = Object.keys(M.parInd).map(par => ({ par, ind: M.parInd[par], w: M.pw[par], type: M.parType[par] })).sort((a, b) => seqOf(a.ind) - seqOf(b.ind) || b.w - a.w);
    return { ind: wr, params };
  }

  function projectList() {
    return [...M.hasData].sort().map(p => ({ p, rid: (M.ipBy[p] || {}).rid || "", partner: (M.ipBy[p] || {}).partner || "" }));
  }

  return { init, band, submissions, fyOptions, ridOptions, projectsFor, oecd, projectScore, activity, trace, weightsFor, projectList, toolSort, seqOf, SRC_LABEL, get M() { return M; } };
})();
export default HC;

